import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';

import useToggle from 'next/hooks/use-toggle';
import { Icon } from 'next/components/ui';
import { removeTagFromFlow } from 'actions/account/flows';
import { getThemeFor } from 'utils';

import { selectTagsForFlow } from 'reducers/account/tags';
import Tag, { TagTypes } from 'components/Common/Tag';
import FlowTag from './FlowTag';
import ManageTagsModal from './ManageTagsModal';

const MAX_TAGS_DISPLAYED = 3;

const StyledTag = styled(Tag)`
  display: flex;
  align-items: center;
  font-weight: normal;
  font-size: 0.75em;
  height: 24px;
  background-color: ${getThemeFor('$gray-10')};
  min-width: unset;
  &:hover {
    background-color: ${getThemeFor('$purple-med')};
    font-weight: bold;
  }
`;

const AddTagButton = styled.button`
  border: none;
  font-size: 12px;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25em;
  background-color: ${getThemeFor('$gray-10')}
  height: 24px;
  cursor: pointer;
  margin-left: 0.25em;
  &:hover {
    opacity: 1;
    background-color: ${getThemeFor('$green-dark')};
    .fa-plus {
      color: ${getThemeFor('$gray-10')};
    }
  }
`;

const AddTagButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 25px;
  span {
    transition: all 0.2s ease;
    opacity: 0;
    white-space: nowrap;
  }
  &:hover {
    span {
      margin-left: 0.5em;
      opacity: 1;
    }
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  flex-grow: 1;
  justify-content: flex-start;
  align-items: center;
  pointer-events: none;
  margin-top: 0.25em;
  > * {
    margin-right: 0.5em;
    pointer-events: auto;
  }
`;

export function StepTagControl({ stepId, stepTags, onRemoveTag }) {
  const [showTagModal, toggleShowTagModal] = useToggle();
  // Given the fact that when we delete a tag from the tag manager
  // we are not deleting their references in the flow,
  // we are not displaying tags without a reference (no name)
  // since the "not longer exists" for that flow
  const cleanTags = stepTags.filter(tag => tag.name != null);

  return (
    <>
      {showTagModal && (
        <ManageTagsModal flowId={stepId} onClose={toggleShowTagModal} />
      )}
      <Container onClick={e => e.stopPropagation()}>
        {cleanTags
          .filter((tag, t) => t < MAX_TAGS_DISPLAYED)
          .map(stepTag => (
            <div role="listitem" key={stepTag.id}>
              <FlowTag
                id={stepTag.id}
                name={stepTag.name}
                onDelete={onRemoveTag}
              />
            </div>
          ))}
        {cleanTags.length > MAX_TAGS_DISPLAYED && (
          <StyledTag type={TagTypes.GRAY_10} onClick={toggleShowTagModal}>
            +{stepTags.length - MAX_TAGS_DISPLAYED}
          </StyledTag>
        )}
        <div className="add-tag-container">
          <AddTagButtonWrapper>
            <AddTagButton title="Add a tag" onClick={toggleShowTagModal}>
              <Icon icon="plus" />
            </AddTagButton>
            <span>Add a tag</span>
          </AddTagButtonWrapper>
        </div>
      </Container>
    </>
  );
}

StepTagControl.propTypes = {
  stepId: PropTypes.string,
  stepTags: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })
  ),
  onRemoveTag: PropTypes.func,
};

const mapStateToProps = (state, { stepId }) => ({
  stepTags: selectTagsForFlow(state, stepId),
});

const mapDispatchToProps = (dispatch, { stepId: flowId }) => ({
  onRemoveTag: tagId => dispatch(removeTagFromFlow({ flowId, tagId })),
});

const ConnectedStepTagControl = connect(
  mapStateToProps,
  mapDispatchToProps
)(StepTagControl);

ConnectedStepTagControl.propTypes = {
  stepId: PropTypes.string,
};

export default ConnectedStepTagControl;
